import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  LayoutDashboard, FileText, CreditCard, MessageSquare, 
  Users, X, ChevronRight, RefreshCw, LogOut, Search,
  Mail, Phone, Building, Calendar, DollarSign, CheckCircle,
  Clock, Filter, Images, BarChart3, Mic
} from 'lucide-react';
import { toast } from 'sonner';
import { AdminPhotoManager } from './AdminPhotoManager';
import { AdminAnalytics } from './AdminAnalytics';
import { AdminConversations } from './AdminConversations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const tabs = [
  { id: 'analytics', name: 'Traffic Analytics', icon: BarChart3, color: '#9B59B6' },
  { id: 'conversations', name: 'Voice Calls', icon: Mic, color: '#F39C12' },
  { id: 'leads', name: 'Lead Forms', icon: Users, color: '#D4AF37' },
  { id: 'questionnaires', name: 'Questionnaires', icon: FileText, color: '#50C878' },
  { id: 'contacts', name: 'Contact Messages', icon: MessageSquare, color: '#007AFF' },
  { id: 'payments', name: 'Payments', icon: CreditCard, color: '#E4405F' },
  { id: 'gallery', name: 'Photo Gallery', icon: Images, color: '#FF6B6B' },
];

export const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [data, setData] = useState({
    leads: [],
    questionnaires: [],
    contacts: [],
    payments: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [leadsRes, questionnairesRes, contactsRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/leads`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/questionnaires`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/contacts`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/payments`).catch(() => ({ data: [] }))
      ]);

      setData({
        leads: Array.isArray(leadsRes.data) ? leadsRes.data : leadsRes.data?.submissions || [],
        questionnaires: Array.isArray(questionnairesRes.data) ? questionnairesRes.data : questionnairesRes.data?.submissions || [],
        contacts: Array.isArray(contactsRes.data) ? contactsRes.data : contactsRes.data?.submissions || [],
        payments: Array.isArray(paymentsRes.data) ? paymentsRes.data : paymentsRes.data?.orders || []
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminAuthTime');
    onLogout();
  };

  const getFilteredData = () => {
    const currentData = data[activeTab] || [];
    if (!searchTerm) return currentData;
    
    return currentData.filter(item => {
      const searchStr = JSON.stringify(item).toLowerCase();
      return searchStr.includes(searchTerm.toLowerCase());
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderListItem = (item, type) => {
    switch (type) {
      case 'leads':
        return (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">{item.fullName || 'Anonymous'}</p>
              <p className="text-white/50 text-sm">{item.email || item.q1?.substring(0, 40) + '...'}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#D4AF37]">{item.q2?.substring(0, 20) || 'Lead Form'}</p>
              <p className="text-xs text-white/40">{formatDate(item.created_at)}</p>
            </div>
          </div>
        );
      case 'questionnaires':
        return (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">{item.fullName}</p>
              <p className="text-white/50 text-sm">{item.sector}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#50C878]">{item.investmentBudget || 'Not specified'}</p>
              <p className="text-xs text-white/40">{formatDate(item.created_at)}</p>
            </div>
          </div>
        );
      case 'contacts':
        return (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">{item.name}</p>
              <p className="text-white/50 text-sm">{item.inquiry_type}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#007AFF]">{item.email}</p>
              <p className="text-xs text-white/40">{formatDate(item.created_at)}</p>
            </div>
          </div>
        );
      case 'payments':
        return (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">{item.customer_name}</p>
              <p className="text-white/50 text-sm">{item.package_name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-[#50C878]">₹{(item.amount / 100).toLocaleString()}</p>
              <p className={`text-xs ${item.status === 'paid' ? 'text-[#50C878]' : 'text-yellow-500'}`}>
                {item.status?.toUpperCase()}
              </p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderDetailView = (item, type) => {
    if (!item) return null;

    const DetailRow = ({ label, value, icon: Icon }) => (
      <div className="flex items-start gap-3 py-3 border-b border-white/10">
        {Icon && <Icon size={16} className="text-[#D4AF37] mt-0.5" />}
        <div className="flex-1">
          <p className="text-xs text-white/50">{label}</p>
          <p className="text-sm text-white mt-0.5">{value || 'N/A'}</p>
        </div>
      </div>
    );

    switch (type) {
      case 'leads':
        return (
          <div className="space-y-1">
            <DetailRow label="Full Name" value={item.fullName} icon={Users} />
            <DetailRow label="Email" value={item.email} icon={Mail} />
            <DetailRow label="Phone" value={item.phone} icon={Phone} />
            <DetailRow label="City" value={item.city} />
            <DetailRow label="Company" value={item.company} icon={Building} />
            <DetailRow label="Designation" value={item.designation} />
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-xs text-[#D4AF37] font-semibold mb-3">QUESTIONNAIRE RESPONSES</p>
              <DetailRow label="Who they are" value={item.q1} />
              <DetailRow label="Sector Interest" value={item.q2} />
              <DetailRow label="Planning Stage" value={item.q3} />
              <DetailRow label="Africa Experience" value={item.q4} />
              <DetailRow label="Support Needed" value={item.q5} />
            </div>
            <DetailRow label="Submitted At" value={formatDate(item.created_at)} icon={Calendar} />
          </div>
        );
      case 'questionnaires':
        return (
          <div className="space-y-1">
            <DetailRow label="Full Name" value={item.fullName} icon={Users} />
            <DetailRow label="Email" value={item.email} icon={Mail} />
            <DetailRow label="Phone" value={item.phone} icon={Phone} />
            <DetailRow label="Company" value={item.company} icon={Building} />
            <DetailRow label="Role" value={item.role} />
            <DetailRow label="Sector" value={item.sector} />
            <DetailRow label="Attraction to Africa" value={item.attraction} />
            <DetailRow label="Investment Budget" value={item.investmentBudget} icon={DollarSign} />
            <DetailRow label="Region Preference" value={item.regionPreference} />
            <DetailRow label="Timeline" value={item.timeline} icon={Clock} />
            <DetailRow label="Business Description" value={item.businessDescription} />
            <DetailRow label="Submitted At" value={formatDate(item.created_at)} icon={Calendar} />
          </div>
        );
      case 'contacts':
        return (
          <div className="space-y-1">
            <DetailRow label="Name" value={item.name} icon={Users} />
            <DetailRow label="Email" value={item.email} icon={Mail} />
            <DetailRow label="Company" value={item.company} icon={Building} />
            <DetailRow label="Inquiry Type" value={item.inquiry_type} />
            <div className="py-3 border-b border-white/10">
              <p className="text-xs text-white/50">Message</p>
              <p className="text-sm text-white mt-2 p-3 bg-white/5 rounded-lg">{item.message || 'N/A'}</p>
            </div>
            <DetailRow label="Submitted At" value={formatDate(item.created_at)} icon={Calendar} />
          </div>
        );
      case 'payments':
        return (
          <div className="space-y-1">
            <div className="text-center py-4 mb-4 bg-[#50C878]/10 rounded-xl">
              <p className="text-3xl font-bold text-[#50C878]">₹{(item.amount / 100).toLocaleString()}</p>
              <p className="text-sm text-white/60 mt-1">{item.package_name}</p>
              <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
                item.status === 'paid' ? 'bg-[#50C878]/20 text-[#50C878]' : 'bg-yellow-500/20 text-yellow-500'
              }`}>
                {item.status?.toUpperCase()}
              </span>
            </div>
            <DetailRow label="Customer Name" value={item.customer_name} icon={Users} />
            <DetailRow label="Customer Email" value={item.customer_email} icon={Mail} />
            <DetailRow label="Customer Phone" value={item.customer_phone} icon={Phone} />
            <DetailRow label="Order ID" value={item.order_id} />
            <DetailRow label="Payment ID" value={item.payment_id} />
            <DetailRow label="Currency" value={item.currency} />
            <DetailRow label="Created At" value={formatDate(item.created_at)} icon={Calendar} />
            {item.paid_at && <DetailRow label="Paid At" value={formatDate(item.paid_at)} icon={CheckCircle} />}
          </div>
        );
      default:
        return null;
    }
  };

  const filteredData = getFilteredData();
  const currentTab = tabs.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#111] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
              <LayoutDashboard size={20} className="text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Dashboard</h1>
              <p className="text-xs text-white/50">Kirit Sobti Advisory</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              disabled={isLoading}
              className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 hover:text-white transition-colors"
              title="Refresh Data"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {tabs.map(tab => (
            <div
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`p-4 rounded-xl cursor-pointer transition-all ${
                activeTab === tab.id 
                  ? 'bg-white/10 border-2' 
                  : 'bg-[#111] border border-white/10 hover:border-white/20'
              }`}
              style={{ borderColor: activeTab === tab.id ? tab.color : undefined }}
            >
              <div className="flex items-center justify-between mb-2">
                <tab.icon size={20} style={{ color: tab.color }} />
                {tab.id !== 'gallery' && (
                  <span className="text-2xl font-bold text-white">{data[tab.id]?.length || 0}</span>
                )}
              </div>
              <p className="text-sm text-white/60">{tab.name}</p>
            </div>
          ))}
        </div>

        {/* Gallery Tab - Special handling */}
        {activeTab === 'gallery' ? (
          <AdminPhotoManager />
        ) : activeTab === 'analytics' ? (
          <AdminAnalytics />
        ) : activeTab === 'conversations' ? (
          <AdminConversations />
        ) : (
          <>
            {/* Search & Filter */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  placeholder={`Search ${currentTab?.name.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#111] border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]"
                />
              </div>
            </div>

        {/* Content */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* List */}
          <div className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="font-semibold text-white flex items-center gap-2">
                {currentTab && <currentTab.icon size={18} style={{ color: currentTab.color }} />}
                {currentTab?.name}
              </h2>
              <span className="text-sm text-white/50">{filteredData.length} items</span>
            </div>
            
            <div className="max-h-[600px] overflow-y-auto">
              {isLoading ? (
                <div className="p-8 text-center">
                  <RefreshCw size={24} className="animate-spin text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white/50">Loading...</p>
                </div>
              ) : filteredData.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-white/50">No {currentTab?.name.toLowerCase()} found</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {filteredData.map((item, index) => (
                    <div
                      key={item.id || index}
                      onClick={() => setSelectedItem(item)}
                      className={`p-4 cursor-pointer transition-colors hover:bg-white/5 ${
                        selectedItem?.id === item.id ? 'bg-white/10' : ''
                      }`}
                    >
                      {renderListItem(item, activeTab)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Detail View */}
          <div className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="font-semibold text-white">Details</h2>
              {selectedItem && (
                <button
                  onClick={() => setSelectedItem(null)}
                  className="p-1 rounded-lg hover:bg-white/10 text-white/50 hover:text-white"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            
            <div className="p-4 max-h-[600px] overflow-y-auto">
              {selectedItem ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {renderDetailView(selectedItem, activeTab)}
                </motion.div>
              ) : (
                <div className="h-full flex items-center justify-center text-white/40 py-20">
                  <div className="text-center">
                    <ChevronRight size={32} className="mx-auto mb-2 opacity-50" />
                    <p>Select an item to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};
